import React from 'react';
import { List, Empty, Tooltip, Badge, Typography, Spin, Modal } from 'antd';
import { PlusOutlined, ClockCircleOutlined, MessageOutlined, LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import moment from 'moment';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import Button from '../../../../shared/globalStyling/Button';
import { urgencyColors, TaskStatusTag } from '../../../Tasks/utils/task.utils';

const { Text } = Typography;

const TaskThreadView = ({
  tasks,
  tasksLoading,
  tasksError,
  handleCreateTask,
  modalVisible,
  onModalClose,
}) => {
  const renderTaskItem = (task) => {
    const commentCount = task.attachedComments?.length || 0;

    return (
      <List.Item
        key={task.id}
        style={{
          borderBottom: '1px solid #f0f0f0',
          padding: '16px',
        }}
      >
        <div style={{ width: '100%', display: 'flex', gap: 12 }}>
          {/* Urgency Badge - Left aligned */}
          <div style={{ paddingTop: 4 }}>
            <Tooltip title={task.urgency ? `URGENCY: ${task.urgency}` : 'No urgency set'}>
              <Badge
                dot
                color={urgencyColors[task.urgency] || 'white'}
              />
            </Tooltip>
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Link
                    to={`/tasks/details/${task.id}`}
                    style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#3b82f6',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    {task.name}
                  </Link>
                  <TaskStatusTag status={task.status} />
                </div>
                {task.requestType && (
                  <Text style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {task.requestType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                    {task.requestSubType && ` - ${task.requestSubType}`}
                  </Text>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {task.assigneeUsers && task.assigneeUsers.length > 0 && (
                  <Tooltip title={task.assigneeUsers.map(u => startCase(u.name || u.username)).join(', ')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {task.assigneeUsers.slice(0, 2).map((user, idx) => (
                        <AvatarS3
                          key={user.id || idx}
                          imgKey={user.avatar && user.avatar.key}
                          username={user.name || user.username}
                          avatarProps={{ size: 24 }}
                        />
                      ))}
                      {task.assigneeUsers.length > 2 && (
                        <Text style={{ fontSize: '12px', color: '#64748b', marginLeft: 4 }}>
                          +{task.assigneeUsers.length - 2}
                        </Text>
                      )}
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '12px', color: '#64748b' }}>
                <ClockCircleOutlined />
                <Text style={{ fontSize: '12px', color: '#64748b' }}>
                  {moment(task.createdAt).format('MMM D, YYYY')}
                </Text>
              </div>
              {task.dueDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '12px' }}>
                  <Text style={{ fontSize: '12px', color: moment(task.dueDate).isBefore(moment()) ? '#ef4444' : '#64748b' }}>
                    Due: {moment(task.dueDate).format('MMM D, YYYY')}
                  </Text>
                </div>
              )}
              <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                <MessageOutlined />
                {commentCount} {commentCount === 1 ? 'Comment' : 'Comments'}
              </div>
              {task.linkUrl && (
                <Tooltip title="External Link">
                  <a href={task.linkUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <LinkOutlined style={{ fontSize: '14px', color: '#3b82f6' }} />
                  </a>
                </Tooltip>
              )}
            </div>

            {task.taskDetails && (
              <div style={{ marginTop: 8 }}>
                <Text
                  style={{
                    fontSize: '13px',
                    color: '#475569',
                    display: 'block',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '40px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {task.taskDetails}
                </Text>
              </div>
            )}
          </div>
        </div>
      </List.Item>
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>Task Thread</Text>
          <Button
            type="primary"
            ghost
            icon={<PlusOutlined />}
            onClick={handleCreateTask}
            style={{
              margin: '0',
            }}
          >
            Create Task
          </Button>
        </div>
      }
      open={modalVisible}
      onCancel={onModalClose}
      closable={false}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onModalClose} type="default">
            Close
          </Button>
        </div>
      }
      width={900}
      bodyStyle={{
        padding: 0,
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      {tasksLoading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
        </div>
      ) : tasksError ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text style={{ color: '#ef4444' }}>Error loading tasks: {tasksError.message}</Text>
        </div>
      ) : tasks.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Text style={{ color: '#94a3b8', display: 'block', marginBottom: 8 }}>
                No tasks linked to this campaign yet
              </Text>
              <Text style={{ fontSize: '13px', color: '#cbd5e1' }}>
                Create a task and link it to this campaign to start tracking work
              </Text>
            </div>
          }
          style={{ padding: '48px 24px' }}
        />
      ) : (
        <List
          dataSource={tasks}
          renderItem={renderTaskItem}
        />
      )}
    </Modal>
  );
};

export default TaskThreadView;
